import React, { useEffect } from 'react';
import { Grid, Stack, TextField } from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { WheelsFilters, WheelsPrice, WheelsVehicleTypeId } from '../../../domain/wheels';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useTranslation } from 'react-i18next';
import { useWheelsPricingConfig } from '../hooks';
import { WheelsZonesSelect } from './wheels-zones-select';
import { WheelsVehiclesSelect } from './wheels-vehicles-select';
import { WheelsMap } from './wheels-map';
import { WheelsVehiclesCapacity } from './wheels-vehicles-capacity';

interface Props {
  formManger: UseFormReturn<WheelsFilters>;
  onWheelsPriceChange: (wheelsPrice: WheelsPrice) => void;
}

export const WheelsFiltersForm = ({ formManger, onWheelsPriceChange }: Props) => {
  const { t } = useTranslation();

  const { pricingConfig } = useWheelsPricingConfig();

  // == Extensions ===============================================================
  dayjs.extend(isSameOrAfter);

  // == Effects ===================================================================
  // As the zone is selected, set the zone display value to the form.
  // This is done to show the user the selected zone display value
  // in other parts of the application without having to make another
  // request to get the zone display value.
  const zone = formManger.watch('zone');
  useEffect(() => {
    if (!zone) return;
    const selectedZone = pricingConfig?.zones.find((z) => z.name === zone);

    if (!selectedZone) return;
    // Set the zone display value to the form
    formManger.setValue('zoneDisplay', selectedZone.display);
  }, [zone, pricingConfig]);

  //if the exitDate field has a date before today, shows the error
  const exitDate = formManger.watch('exitDate');
  useEffect(() => {
      if (dayjs(exitDate).isBefore(dayjs(), 'day')) {
        formManger.setError('exitDate', { message: t('wheels_service.inputs.exit_date_error') });
        return;
      }else formManger.clearErrors('exitDate');

  }, [exitDate, formManger]);


  // == Handlers ==================================================================
  const validateExitTime = (value: string) => {
      const time = dayjs(value, 'HH:mm');
      const minTime = dayjs().hour(9).minute(0);  // 09:00 AM
      const maxTime = dayjs().hour(23).minute(0); // 11:00 PM
  
      if (time.isBefore(minTime) || time.isAfter(maxTime)) {
        formManger.setError('exitTime', { message: t('wheels_service.inputs.exit_time_error') });
        return false;
      }else formManger.clearErrors('exitTime');
      return true;
    }

  const handleExitDateChange = (onChange: (date: string) => void) => (date: Dayjs | null) => {
    if (!date) return;
    if (!date.isValid()) return;

    onChange(date.format('YYYY-MM-DD'));
  };

  const handleExitTimeChange = (onChange: (time: string) => void) => (time: Dayjs | null) => {
    if (!time) return;
    if (!time.isValid()) return;
    validateExitTime(time.format('HH:mm'));

    onChange(time.format('HH:mm'));
  };

  const handleVehicleChange =
    (onChange: (vehicle: WheelsVehicleTypeId) => void) => (vehicle: WheelsVehicleTypeId, wheelsPrice: WheelsPrice) => {
      onChange(vehicle);
      onWheelsPriceChange(wheelsPrice);
    };

  // == Render ==================================================================
  return (
    <FormProvider {...formManger}>
      <Grid container columns={4} spacing={3}>
        <Grid item xs={4} md={2}>
          <Controller
            control={formManger.control}
            name="exitDate"
            rules={{ required: true, validate: (value) => dayjs(value).isSameOrAfter(dayjs(), 'day'),}}
            render={({ field }) => (
              <DatePicker
                value={field.value ? dayjs(field.value) : null}
                label={t('wheels_service.inputs.exit_date')}
                minDate={dayjs()}
                sx={{ width: '100%' }}
                onChange={handleExitDateChange(field.onChange)}
                disablePast
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    error: !!formManger.formState.errors.exitDate, 
                    onBlur: field.onBlur,
                    helperText: formManger.formState.errors.exitDate
                      ? t(`${formManger.formState.errors.exitDate.message}`)
                      : '',
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={4} md={2}>
          <Controller
            control={formManger.control}
            name="exitTime"
            rules={{ required: true, validate: validateExitTime }}
            render={({ field, fieldState }) => (
              <TimePicker
                ampm={false}
                minTime={dayjs(new Date(0, 0, 0, 9))}
                maxTime={dayjs(new Date(0, 0, 0, 23, 30))}
                timeSteps={{ minutes: 30 }}
                value={field.value ? dayjs(field.value, 'HH:mm') : null}
                label={t('wheels_service.inputs.exit_time')}
                sx={{ width: '100%' }}
                onChange={handleExitTimeChange(field.onChange)}
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    error: !!fieldState?.error,
                    onBlur: field.onBlur,
                    helperText: fieldState?.error
                      ? t(`${fieldState?.error.message}`)
                      : '',
                  },
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={4} md={4}>
          <WheelsZonesSelect control={formManger.control} zones={pricingConfig?.zones ?? []} />
        </Grid>

        <Grid item xs={4} md={1}>
          <Controller
            control={formManger.control}
            name="city"
            rules={{ required: true, minLength: 4 }}
            render={({ field }) => (
              <TextField
                label={t('wheels_service.inputs.city')}
                value={field.value}
                fullWidth
                onChange={field.onChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={4} md={2}>
          <Controller
            control={formManger.control}
            name="address"
            rules={{ required: true, minLength: 5 }}
            render={({ field }) => (
              <TextField
                label={t('wheels_service.inputs.direction')}
                value={field.value}
                fullWidth
                onChange={field.onChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={4} md={1}>
          <Controller
            control={formManger.control}
            name="zipCode"
            rules={{ required: true, minLength: 4 }}
            render={({ field }) => (
              <TextField
                label={t('wheels_service.inputs.zipcode')}
                value={field.value}
                fullWidth
                onChange={field.onChange}
              />
            )}
          />
        </Grid>

        <Grid item xs={4} md={2}>
          <Stack alignItems="center">
            <WheelsMap sx={{ maxWidth: '400px' }} />
          </Stack>
        </Grid>

        <Grid item xs={4} md={2}>
          <Controller
            control={formManger.control}
            name="vehicleType"
            rules={{ required: true }}
            render={({ field }) => (
              <WheelsVehiclesSelect
                value={field.value}
                pricingConfig={pricingConfig}
                zone={formManger.watch('zone')}
                onChange={handleVehicleChange(field.onChange)}
              />
            )}
          />
          <WheelsVehiclesCapacity vehicles={pricingConfig?.vehicleTypes ?? []} />
        </Grid>
      </Grid>
    </FormProvider>
  );
};
